/* CarryOn — theme.js
   Small vanilla custom elements. No framework, no globals beyond window.routes
   and window.moneyFormat, both set in layout/theme.liquid. */
(function () {
  'use strict';

  var routes = window.routes || {};
  var MONEY = window.moneyFormat || '${{amount}}';

  /* ---------- money ---------- */
  function formatMoney(cents, format) {
    var fmt = format || MONEY;
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    function group(decimals, thousands, decimal) {
      var n = (cents / 100).toFixed(decimals);
      var parts = n.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholder);
    var value;
    switch (match && match[1]) {
      case 'amount_no_decimals': value = group(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = group(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = group(0, '.', ','); break;
      case 'amount_with_space_separator': value = group(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = group(0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = group(2, "'", '.'); break;
      default: value = group(2, ',', '.');
    }
    return fmt.replace(placeholder, value);
  }

  /* "Save £54.00 (11%)" - built from the translated sentence in themeStrings,
     never stitched together from fragments here. Percent is worked out against
     the higher price, which is the number a shopper is comparing to. */
  function savingLabel(was, now, style) {
    var strings = window.themeStrings || {};
    var saved = was - now;
    if (saved <= 0) return '';
    var percent = was > 0 ? Math.round((saved / was) * 100) : 0;
    var money = formatMoney(saved);

    /* a saving too small to round up to 1% reads as "Save 0%", so show the money */
    var wanted = percent > 0 ? (style || 'both') : 'money';
    var template = wanted === 'percent'
      ? (strings.savePercent || 'Save [[percent]]%')
      : wanted === 'money'
        ? (strings.saveAmount || 'Save [[amount]]')
        : (strings.saveBoth || 'Save [[amount]] ([[percent]]%)');

    return template.replace('[[amount]]', money).replace('[[percent]]', percent);
  }

  function parseJSONScript(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function announce(message) {
    var region = document.getElementById('live-region');
    if (region) region.textContent = message;
  }

  /* ---------- header nav ---------- */
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-nav-toggle]');
    if (!toggle) return;
    var nav = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!nav) return;
    var open = nav.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* ---------- announcement ticker ----------
     Measures one run of messages and clones it until the track is wider than
     the screen, so the loop never shows a gap however few messages there are.
     Scrolls by exactly one run's width, which is what makes the seam invisible. */
  class AnnouncementTicker extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.announcement-bar__track');
      this.run = this.querySelector('.announcement-bar__run');
      if (!this.track || !this.run) return;
      this.build();
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.schedule.bind(this));
        this.observer.observe(this);
      } else {
        window.addEventListener('resize', this.schedule.bind(this));
      }
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.timer) clearTimeout(this.timer);
    }
    schedule() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(this.build.bind(this), 150);
    }
    build() {
      /* start from one run every time, so repeated resizes cannot pile up clones */
      Array.from(this.track.children).slice(1).forEach(function (n) { n.remove(); });

      var width = this.run.getBoundingClientRect().width;
      if (!width) return;

      /* enough copies that one run's width of travel always leaves the viewport
         covered: the visible window plus the run that is scrolling out */
      var needed = Math.ceil((this.getBoundingClientRect().width + width) / width) + 1;
      for (var i = 1; i < needed; i++) {
        var clone = this.run.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
      }

      this.style.setProperty('--run-width', width + 'px');
      this.style.setProperty('--ticker-duration', (Number(this.dataset.speed) || 34) + 's');
      this.setAttribute('data-ready', 'true');
    }
  }
  customElements.define('announcement-ticker', AnnouncementTicker);

  /* ---------- cart count ---------- */
  function refreshCartCount() {
    if (!routes.cart) return;
    fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        document.querySelectorAll('[data-cart-count]').forEach(function (node) {
          node.textContent = cart.item_count;
          node.hidden = cart.item_count === 0;
        });
      })
      .catch(function () { /* the badge just stays as rendered */ });
  }

  function addToCart(items) {
    return fetch(routes.cart_add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return response.json().then(function (body) {
        if (!response.ok) throw new Error(body.description || body.message || 'Could not add to cart');
        return body;
      });
    });
  }

  /* ---------- gallery ---------- */
  class GalleryViewer extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.gallery__thumb'));
      this.thumbs.forEach(function (thumb, index) {
        thumb.addEventListener('click', function () { this.show(index); }.bind(this));
      }, this);
      document.addEventListener('carryon:variant', this.onVariant.bind(this));
    }
    show(index) {
      this.slides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === index); });
      this.thumbs.forEach(function (thumb, i) { thumb.setAttribute('aria-current', i === index ? 'true' : 'false'); });
    }
    onVariant(event) {
      var mediaId = event.detail && event.detail.variant && event.detail.variant.featured_media_id;
      if (!mediaId) return;
      var index = this.slides.findIndex(function (slide) { return slide.dataset.mediaId === String(mediaId); });
      if (index > -1) this.show(index);
    }
  }
  customElements.define('gallery-viewer', GalleryViewer);

  /* ---------- price block ----------
     The price is server-rendered for the first variant; without this it stayed
     on that first variant's price while everything else on the page moved. */
  function paintPrice(root, variant) {
    if (!root) return;
    var now = root.querySelector('[data-price-now]');
    var was = root.querySelector('[data-price-was]');
    var save = root.querySelector('[data-price-save]');
    var compare = variant.compare_at_price || 0;
    var onSale = compare > variant.price;

    var write = function (node, text) {
      if (!node) return;
      var slot = node.querySelector('[data-price-value]') || node;
      slot.textContent = text;
    };

    write(now, formatMoney(variant.price));
    root.classList.toggle('price--sale', onSale);
    write(was, onSale ? formatMoney(compare) : '');
    if (was) was.hidden = !onSale;
    if (save) {
      var label = onSale ? savingLabel(compare, variant.price, root.dataset.saving) : '';
      save.textContent = label;
      save.hidden = !label;
    }
  }

  /* The price block is plain markup, so bind any that exist on the page. */
  document.addEventListener('carryon:variant', function (event) {
    var v = event.detail && event.detail.variant;
    if (!v) return;
    document.querySelectorAll('[data-price]').forEach(function (node) { paintPrice(node, v); });
  });

  /* ---------- variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = parseJSONScript(this, '[data-variants]') || [];
      this.form = document.getElementById(this.dataset.form);
      this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.selected = this.variants.map(function () { return null; });
      this.options = [];
      var current = this.variants.find(function (v) { return String(v.id) === this.dataset.selected; }, this) || this.variants[0];
      if (current) this.options = current.options.slice();
      this.addEventListener('click', this.onClick.bind(this));
      this.render();
    }
    onClick(event) {
      var button = event.target.closest('[data-option-index]');
      if (!button) return;
      event.preventDefault();
      this.setOption(Number(button.dataset.optionIndex), button.dataset.value);
    }
    setOption(index, value) {
      if (index < 0 || value == null) return;
      this.options[index] = value;
      this.render();
    }
    match(options) {
      return this.variants.find(function (variant) {
        return variant.options.every(function (value, i) { return options[i] == null || value === options[i]; });
      });
    }
    render() {
      var self = this;
      this.querySelectorAll('[data-option-index]').forEach(function (button) {
        var index = Number(button.dataset.optionIndex);
        var chosen = self.options[index] === button.dataset.value;
        button.setAttribute('aria-checked', chosen ? 'true' : 'false');
        var probe = self.options.slice();
        probe[index] = button.dataset.value;
        var candidate = self.match(probe);
        button.dataset.unavailable = candidate && candidate.available ? 'false' : 'true';
      });
      this.querySelectorAll('[data-option-value-for]').forEach(function (label) {
        label.textContent = self.options[Number(label.dataset.optionValueFor)] || '';
      });
      var variant = this.match(this.options);
      if (!variant) return;
      if (this.idInput) this.idInput.value = variant.id;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      document.dispatchEvent(new CustomEvent('carryon:variant', { detail: { variant: variant } }));
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- quantity ---------- */
  class QuantityStepper extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', function (event) {
        var button = event.target.closest('button[data-step]');
        if (!button || !this.input) return;
        event.preventDefault();
        var next = Number(this.input.value || 1) + Number(button.dataset.step);
        this.input.value = Math.max(Number(this.input.min || 1), next);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }.bind(this));
    }
  }
  customElements.define('quantity-stepper', QuantityStepper);

  /* ---------- product form ---------- */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();

    var button = form.querySelector('[data-add-button]');
    var errorNode = form.querySelector('[data-form-error]');
    var items = [{
      id: Number(form.querySelector('input[name="id"]').value),
      quantity: Number((form.querySelector('input[name="quantity"]') || {}).value || 1) || 1
    }];

    if (errorNode) { errorNode.textContent = ''; errorNode.hidden = true; }
    if (button) {
      if (!button.dataset.label) button.dataset.label = button.textContent.trim();
      button.setAttribute('aria-disabled', 'true');
      button.textContent = 'Adding…';
    }

    addToCart(items)
      .then(function () {
        announce('Added to cart');
        var toCartField = form.querySelector('input[name="to_cart"]');
        var goToCart = toCartField
          ? toCartField.value !== 'false'
          : !!(window.themeSettings && window.themeSettings.cartRedirect);

        if (goToCart) {
          /* Automatic discounts set up in Shopify apply themselves once the
             cart total qualifies, so the cart is where the shopper sees them. */
          window.location.href = routes.cart;
          return;
        }
        refreshCartCount();
      })
      .catch(function (error) {
        if (errorNode) { errorNode.textContent = error.message; errorNode.hidden = false; }
        announce(error.message);
      })
      .finally(function () {
        if (button) { button.removeAttribute('aria-disabled'); if (button.dataset.label) button.textContent = button.dataset.label; }
      });
  });

  /* ---------- sticky buy bar ---------- */
  class StickyBar extends HTMLElement {
    connectedCallback() {
      var anchor = document.getElementById(this.dataset.watch);
      this.priceNode = this.querySelector('[data-sticky-price]');
      if (!anchor || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(function (entries) {
        this.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
      }.bind(this), { threshold: 0 }).observe(anchor);
      document.addEventListener('carryon:variant', function (event) {
        var v = event.detail && event.detail.variant;
        if (this.priceNode && v) this.priceNode.textContent = formatMoney(v.price);
      }.bind(this));
      this.addEventListener('click', function (event) {
        if (!event.target.closest('[data-sticky-add]')) return;
        event.preventDefault();
        var form = document.getElementById(this.dataset.form);
        if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }.bind(this));
    }
  }
  customElements.define('sticky-bar', StickyBar);

  /* ---------- airline size checker ----------
     Compares the folded dimensions against the airline's cabin allowance on
     each axis. Every airline publishes its own numbers, so the merchant edits
     them as blocks; nothing here is hard-coded. */
  class AirlineChecker extends HTMLElement {
    connectedCallback() {
      this.airlines = parseJSONScript(this, '[data-airlines]') || [];
      this.dims = (this.dataset.dims || '').split('x').map(function (n) { return parseFloat(n.trim()); });
      this.select = this.querySelector('select');
      this.result = this.querySelector('.checker__result');
      if (this.select) {
        this.select.addEventListener('change', this.render.bind(this));
        this.render();
      }
    }
    render() {
      var airline = this.airlines.find(function (item) { return item.handle === this.select.value; }, this);
      if (!airline || !this.result) return;
      var ours = this.dims.slice().sort(function (a, b) { return b - a; });
      var theirs = [airline.length, airline.width, airline.height].map(Number).sort(function (a, b) { return b - a; });
      var labels = ['Longest side', 'Second side', 'Shortest side'];

      var over = ours.map(function (value, i) { return value - theirs[i]; });
      var worst = Math.max.apply(null, over);
      var verdict = worst <= 0 ? 'fits' : (worst <= 2 ? 'tight' : 'gate');

      var copy = {
        fits: {
          title: 'Fits the cabin',
          body: 'Folded, it is inside ' + airline.name + "'s cabin allowance on every side. It goes in the overhead locker like any carry-on — no gate check, no waiting on the jet bridge."
        },
        tight: {
          title: 'Fits, but it is close',
          body: 'It is within ' + worst.toFixed(1) + ' cm of ' + airline.name + "'s limit on the longest side. Most cabin crew wave this through, but on a full flight bring the travel bag so it can be gate-checked without a scratch."
        },
        gate: {
          title: 'Gate-check on this airline',
          body: airline.name + ' runs a smaller cabin allowance than the folded size. Wheel it to the aircraft door, hand it over in the travel bag, and pick it up on the jet bridge on arrival — free on every airline we know of, because it is a pushchair.'
        }
      }[verdict];

      var icon = verdict === 'fits'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 4.5-5"/></svg>'
        : verdict === 'tight'
          ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5"/><path d="M12 16.2h.01"/></svg>'
          : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 17h18"/><path d="M5 17V9l4 2 3-6 3 6 4-2v8"/></svg>';

      var bars = ours.map(function (value, i) {
        var limit = theirs[i];
        var isOver = value > limit;
        var pct = Math.min(100, Math.round((value / Math.max(limit, value)) * 100));
        return '<div class="checker__bar" data-over="' + isOver + '">' +
          '<div class="checker__bar-head"><span>' + labels[i] + '</span>' +
          '<span>' + value + ' cm vs ' + limit + ' cm</span></div>' +
          '<div class="checker__bar-track"><div class="checker__bar-fill" style="width:' + pct + '%"></div></div></div>';
      }).join('');

      this.result.dataset.verdict = verdict;
      this.result.innerHTML =
        '<p class="checker__verdict">' + icon + '<span>' + copy.title + '</span></p>' +
        '<p class="text-muted">' + copy.body + '</p>' +
        '<div class="checker__bars">' + bars + '</div>' +
        (airline.note ? '<p class="text-faint" style="margin-top:16px">' + airline.note + '</p>' : '');
      announce(copy.title + ' on ' + airline.name);
    }
  }
  customElements.define('airline-checker', AirlineChecker);

  /* ---------- reviews: show more ---------- */
  class ReviewList extends HTMLElement {
    connectedCallback() {
      this.step = Number(this.dataset.step || 6);
      this.cards = Array.from(this.querySelectorAll('[data-review]'));
      this.button = this.querySelector('[data-review-more]');
      this.shown = Number(this.dataset.initial || 6);
      this.paint();
      if (this.button) this.button.addEventListener('click', function () {
        this.shown += this.step;
        this.paint();
      }.bind(this));
    }
    paint() {
      this.cards.forEach(function (card, i) { card.hidden = i >= this.shown; }, this);
      if (this.button) this.button.hidden = this.shown >= this.cards.length;
    }
  }
  customElements.define('review-list', ReviewList);

  /* ---------- video facade ---------- */
  document.addEventListener('click', function (event) {
    var play = event.target.closest('[data-video-play]');
    if (!play) return;
    var frame = play.closest('.video-frame');
    var src = frame && frame.dataset.embed;
    if (!src) return;
    var iframe = document.createElement('iframe');
    iframe.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
    iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', frame.dataset.title || 'Video');
    frame.innerHTML = '';
    frame.appendChild(iframe);
  });

  /* ---------- cart line quantity ---------- */
  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-cart-qty]');
    if (!input) return;
    fetch(routes.cart_change, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: input.dataset.key, quantity: Number(input.value) })
    }).then(function () { window.location.reload(); });
  });

  refreshCartCount();
})();
